import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BRAND_CONFIG } from 'constants/branding';
import { partyNamesMatch } from './party';

/**
 * The terms of service, served by the portal itself.
 *
 * <h3>Why this exists</h3>
 * {@code BRAND_CONFIG.terms.url} has been {@code '/terms'} since the brand layer
 * was written, the signup checkbox links to it, and <b>no route ever answered
 * it</b> — so every brand's signup screen asked people to agree to a blank tab.
 * That is the same fault the privacy page was written to fix; terms were missed
 * because only the privacy URL was needed for a directory submission.
 *
 * <h3>Why it is brand-resolved</h3>
 * A reseller's customer contracts with the reseller. Terms signed by the platform
 * owner would name a company they have never dealt with, so the party comes from
 * {@link BRAND_CONFIG} exactly as it does in the policy.
 *
 * <p><b>This states how the product actually operates; it is not legal advice.</b>
 * The commercial terms — notice periods, liability, the governing-law clause — need
 * a lawyer's eye before anybody relies on them, and the sections below are
 * deliberately the factual ones rather than invented boilerplate.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-2 space-y-3 text-sm leading-relaxed text-slate-700">{children}</div>
  </section>
);

const Terms: React.FC = () => {
  const { brandName, privacy } = BRAND_CONFIG;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to sign in
        </Link>

        <header className="mt-6 border-b border-slate-200 pb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Terms and Conditions
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            The terms on which {brandName} is provided to your organisation.
          </p>
          <p className="mt-1 text-xs text-slate-500">Last updated {privacy.lastUpdated}</p>
        </header>

        <Section title="1. Who these terms are with">
          <p>
            {partyNamesMatch()
              ? `${privacy.legalName} ("we") provides this platform.`
              : `${brandName} is provided by ${privacy.legalName} ("we").`}{' '}
            These terms apply to the organisation that holds the account and to everyone who signs
            in under it.
          </p>
          <p>
            Where your organisation has a signed agreement with us, that agreement governs and these
            terms fill the gaps rather than replacing it.
          </p>
        </Section>

        <Section title="2. Your account">
          <p>
            Accounts are issued to named people and are not to be shared. You are responsible for
            what happens under your sign-in, so keep your password to yourself and tell us if you
            think somebody else has it.
          </p>
          <p>
            Actions taken in the platform are recorded against the account that took them, and that
            record is what we rely on when a question arises about who did what.
          </p>
        </Section>

        <Section title="3. Calling and messaging — the part that carries real obligations">
          <p>
            You are responsible for having a lawful basis to contact the people you contact through{' '}
            {brandName}. The person on the other end of a call or a message has a relationship with
            your organisation, not with us.
          </p>
          <p>
            That means: honour do-not-contact requests, respect calling-hour restrictions, keep
            marketing messages inside the registrations and template approvals they require, and
            identify your business on the call. The platform gives you a do-not-contact list and
            suppression controls; using them is yours to do.
          </p>
          <p>
            We may suspend sending for an account that generates serious complaint volumes or
            appears to be contacting people without consent, because a shared sending reputation is
            damaged for every customer by one that does.
          </p>
        </Section>

        <Section title="4. Acceptable use">
          <p>
            Do not use {brandName} to deceive the person being contacted, to impersonate somebody
            else, to send content that is unlawful where it is received, or to disguise who is
            calling. Do not attempt to reach other organisations&rsquo; data, and do not probe or
            load-test the platform without asking us first.
          </p>
        </Section>

        <Section title="5. Connecting an AI assistant">
          <p>
            You may connect a third-party AI assistant to your account. Doing so grants it the
            permissions you tick and nothing else, and what the assistant does with your account is
            treated as done by you. The{' '}
            <Link to="/docs/connector" className="font-medium text-blue-700 hover:text-blue-800">
              connector documentation
            </Link>{' '}
            sets out exactly what each permission allows.
          </p>
          <p>
            Your use of the assistant itself is governed by that provider&rsquo;s own terms, which we
            are not a party to.
          </p>
        </Section>

        <Section title="6. Your data">
          <p>
            The information you put into {brandName}, and the call and message records generated by
            using it, remain yours. We process them to provide the service, as described in our{' '}
            <Link to="/privacy" className="font-medium text-blue-700 hover:text-blue-800">
              privacy policy
            </Link>
            .
          </p>
        </Section>

        <Section title="7. Availability">
          <p>
            We work to keep the platform available but do not promise it will never be interrupted.
            Planned maintenance is announced in the platform where it will be noticeable. Telephony
            and messaging depend on carriers and on WhatsApp, whose own outages we cannot prevent.
          </p>
        </Section>

        <Section title="8. Charges">
          <p>
            Charges, billing period and payment terms are those in your organisation&rsquo;s
            agreement or order. Usage-based charges — calls placed, messages sent — are billed on
            the volumes recorded in the platform, which you can see in your own reports.
          </p>
        </Section>

        <Section title="9. Ending the arrangement">
          <p>
            Either side may end the arrangement in line with your agreement. On termination you can
            export your data; after the retention period in the privacy policy it is deleted.
          </p>
        </Section>

        <Section title="10. Changes to these terms">
          <p>
            If we change these terms we will update the date at the top of this page, and we will
            tell you in the platform where a change materially affects your use of it.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            Questions about these terms go to{' '}
            <a
              className="font-medium text-blue-700 hover:text-blue-800"
              href={`mailto:${privacy.contactEmail}`}
            >
              {privacy.contactEmail}
            </a>
            .
          </p>
          <p>
            {privacy.legalName}
            <br />
            {privacy.address}
          </p>
        </Section>

        <footer className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
          {privacy.legalName}
        </footer>
      </div>
    </div>
  );
};

export default Terms;
